import { yupResolver } from "@hookform/resolvers/yup";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { Button, Notification } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import {
  useDenominationChurches,
  useDenominationHierarchy,
  useDenominationStore
} from "features/denominations";
import { useMessagesStore } from "features/messages";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteEmployee, useEmployee, useUpdateEmployee } from "../api";
import { BankForm } from "../components/Forms/BankForm";
import { CompensationForm } from "../components/Forms/CompensationForm";
import { EducationBackgroundForm } from "../components/Forms/EducationBackgroundForm";
import { EmergencyContactForm } from "../components/Forms/EmergencyContactForm";
import { EmployerInfoForm } from "../components/Forms/EmployerInfoForm";
import { FamilyInfoForm } from "../components/Forms/FamilyInfoForm";
import { JobForm } from "../components/Forms/JobForm";
import { PersonalInfoForm } from "../components/Forms/PersonalInfoForm";
import { employeeSchema } from "../validation";

export const HRProfile = () => {
  const [activeNav, setActiveNav] = useState("personal");
  const [employee, setEmployee] = useState(null);
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );
  const addRecipient = useMessagesStore(state => state.addRecipient);
  const clearRecipients = useMessagesStore(state => state.clearRecipients);
  const addEmployee = useMessagesStore(state => state.addEmployee);
  const removeEmployee = useMessagesStore(state => state.removeEmployee);
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const updateEmployeeMutation = useUpdateEmployee();
  const deleteEmployeeMutation = useDeleteEmployee();

  const { data, isLoading } = useEmployee({
    churchId: selectedChurch?.id,
    employeeId: id,
    isDenomination: window.location.pathname.includes("denomination"),
    denominationId: selectedDenomination?.id
  });

  const denominationId = window.location.pathname.includes("denomination")
    ? selectedDenomination?.id
    : undefined;

  const denominationChurchesQuery = useDenominationChurches({
    denominationId,
    config: {
      enabled: !!denominationId
    }
  });

  const denominationHierarchyQuery = useDenominationHierarchy({
    denominationId,
    config: {
      enabled: !!denominationId
    }
  });

  const churches = useMemo(() => {
    return (
      [
        ...(selectedDenomination ? [selectedDenomination] : []),
        ...(denominationChurchesQuery.data?.result || []),
        ...(denominationHierarchyQuery.data?.result?.data || [])
      ] || []
    );
  }, [denominationChurchesQuery.data, denominationHierarchyQuery.data]);

  useEffect(() => {
    if (data) {
      setEmployee(data.result);
    }
  }, [data]);

  const {
    register,
    control,
    formState: { errors },
    reset,
    handleSubmit,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      employee_id: "",
      title: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      gender: "",
      date_of_birth: "",
      nationality_id: "",
      marital_status: "",
      employee_photo: null,
      phone_number: "",
      email: "",
      address: "",
      position: "",
      employment_date: "",
      employment_type: "",
      salary_bank_name: "",
      salary_bank_account_number: "",
      pf_bank_name: "",
      pf_bank_account_number: "",
      social_id_number: "",
      tin_number: "",
      gross_salary: "",
      overtime: "",
      transport_allowance: "",
      house_allowance: "",
      communication_allowance: "",
      bonus: "",
      tax: "",
      credit: "",
      pension: "",
      provident_fund: "",
      penalty: "",
      emergency_contact_title: "",
      emergency_contact_first_name: "",
      emergency_contact_last_name: "",
      emergency_contact_middle_name: "",
      relationship_to_employee: "",
      emergency_contact_phone: "",
      emergency_contact_address: "",
      has_children: false,
      children: [],
      education_background: [],
      has_experience: false,
      work_experience: [],
      employer_name: "",
      employer_location: "",
      employer_house_number: "",
      employer_phone_number: "",
      mother_name: "",
      date_married: "",
      spouse_name: "",
      spouse_phone_number: "",
      spouse_job_title: "",
      birth_country_id: "",
      birth_region_id: "",
      birth_zone_id: "",
      birth_city_id: "",
      birth_sub_city_id: "",
      birth_woreda_id: "",
      employee_doc: null
    },
    mode: "onBlur",
    resolver: yupResolver(employeeSchema)
  });

  useEffect(() => {
    if (employee) {
      reset({
        employee_id: employee?.employee_id,
        title: employee?.title,
        first_name: employee?.first_name,
        middle_name: employee?.middle_name,
        last_name: employee?.last_name,
        gender: employee?.gender,
        date_of_birth: employee?.date_of_birth
          ? moment(employee.date_of_birth).format("YYYY-MM-DD")
          : "",
        nationality_id: employee?.nationality_id,
        marital_status: employee?.marital_status,
        employee_photo: null,
        phone_number: employee?.phone_number,
        email: employee?.email,
        address: employee?.address,
        position: employee?.position,
        employment_date: employee?.employment_date
          ? moment(employee.employment_date).format("YYYY-MM-DD")
          : "",
        employment_type: employee?.employment_type,
        salary_bank_name: employee?.salary_bank_name,
        salary_bank_account_number: employee?.salary_bank_account_number,
        pf_bank_name: employee?.pf_bank_name,
        pf_bank_account_number: employee?.pf_bank_account_number,
        social_id_number: employee?.social_id_number,
        tin_number: employee?.tin_number,
        gross_salary: employee?.gross_salary,
        overtime: employee?.overtime,
        transport_allowance: employee?.transport_allowance,
        house_allowance: employee?.house_allowance,
        communication_allowance: employee?.communication_allowance,
        bonus: employee?.bonus,
        tax: employee?.tax,
        credit: employee?.credit,
        pension: employee?.pension,
        provident_fund: employee?.provident_fund,
        penalty: employee?.penalty,
        emergency_contact_title: employee?.emergency_contact_title,
        emergency_contact_first_name: employee?.emergency_contact_first_name,
        emergency_contact_last_name: employee?.emergency_contact_last_name,
        emergency_contact_middle_name: employee?.emergency_contact_middle_name,
        relationship_to_employee: employee?.relationship_to_employee,
        emergency_contact_phone: employee?.emergency_contact_phone,
        emergency_contact_address: employee?.emergency_contact_address,
        has_children: String(employee?.has_children),
        children:
          employee?.children?.map(child => ({
            ...child,
            date_of_birth: child.date_of_birth
              ? moment(child.date_of_birth).format("YYYY-MM-DD")
              : ""
          })) || [],
        education_background:
          employee?.education_background?.map(edu => ({
            ...edu,
            start_date: edu.start_date
              ? moment(edu.start_date).format("YYYY-MM-DD")
              : "",
            end_date: edu.end_date
              ? moment(edu.end_date).format("YYYY-MM-DD")
              : ""
          })) || [],
        has_experience: String(employee?.has_experience),
        work_experience:
          employee?.work_experience?.map(exp => ({
            ...exp,
            start_date: exp.start_date
              ? moment(exp.start_date).format("YYYY-MM-DD")
              : "",
            end_date: exp.end_date
              ? moment(exp.end_date).format("YYYY-MM-DD")
              : ""
          })) || [],
        employer_name: employee?.employer_name,
        employer_location: employee?.employer_location,
        employer_house_number: employee?.employer_house_number,
        employer_phone_number: employee?.employer_phone_number,
        mother_name: employee?.mother_name,
        date_married: employee?.date_married
          ? moment(employee.date_married).format("YYYY-MM-DD")
          : "",
        spouse_name: employee?.spouse_name,
        spouse_phone_number: employee?.spouse_phone_number,
        spouse_job_title: employee?.spouse_job_title,
        birth_country_id: employee?.birth_country_id,
        birth_region_id: employee?.birth_region_id,
        birth_zone_id: employee?.birth_zone_id,
        birth_city_id: employee?.birth_city_id,
        birth_sub_city_id: employee?.birth_sub_city_id,
        birth_woreda_id: employee?.birth_woreda_id,
        employee_doc: null
      });
    }
  }, [employee]);

  useEffect(() => {
    if (denominationId && churches.length > 0 && employee) {
      const church = churches.find(
        church =>
          church?.name?.toLowerCase().trim() ===
          employee?.employer_name?.toLowerCase().trim()
      );
      setValue("employer_name", church?.name || "");
    }
  }, [denominationId, churches, setValue, employee]);

  const onSubmit = values => {
    const formData = new FormData();

    // Helper function to format date to ISO format (YYYY-MM-DD)
    const formatDate = date => {
      if (!date) return "";
      return moment(date).format("YYYY-MM-DD");
    };

    // Filter out null/undefined values and convert to FormData
    Object.entries(values)
      .filter(([, value]) => value != null)
      .forEach(([key, value]) => {
        if ((key === "employee_photo" || key === "employee_doc") && value) {
          // Only append file if it has files
          if (value instanceof FileList && value.length > 0) {
            formData.append(key, value[0]);
          } else if (value instanceof File) {
            formData.append(key, value);
          }
        } else if (key === "has_children" || key === "has_experience") {
          formData.append(key, value ? "1" : "0");
        } else if (Array.isArray(value)) {
          // Handle arrays by appending each item with array notation
          value.forEach((item, index) => {
            Object.entries(item).forEach(([itemKey, itemValue]) => {
              if (itemValue != null && itemValue !== "") {
                formData.append(`${key}[${index}][${itemKey}]`, itemValue);
              }
            });
          });
        } else if (value instanceof Date) {
          formData.append(key, formatDate(value));
        } else if (value !== "") {
          formData.append(key, value);
        }
      });

    updateEmployeeMutation.mutate(
      {
        churchId: selectedChurch?.id,
        employeeId: employee?.id,
        employeeData: formData,
        isDenomination: window.location.pathname.includes("denomination"),
        denominationId: selectedDenomination?.id
      },
      {
        onSuccess: () => {
          if (window.location.pathname.includes("denomination")) {
            navigate(`/denomination-dashboard/${selectedDenomination?.id}/hr`);
          } else {
            navigate(`/hr`);
          }
        }
      }
    );
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Stack py={4} spacing={4}>
      <Stack
        direction="row"
        sx={{
          backgroundColor: "rgba(248, 248, 248, 1)"
        }}
      >
        <Avatar
          variant="square"
          src={`${IMAGE_URL}/${employee?.employee_photo}`}
          alt={employee?.first_name}
          sx={{
            height: "auto",
            width: 214
          }}
        />
        <Stack p={2} flex={1} spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" fontWeight={700}>
              {employee?.first_name} {employee?.middle_name}{" "}
              {employee?.last_name}
            </Typography>
            <Button
              style={{ backgroundColor: "#FC5758", borderColor: "#FC5758" }}
              onClick={() =>
                deleteEmployeeMutation.mutate(
                  {
                    churchId: selectedChurch?.id,
                    employeeId: id,
                    isDenomination:
                      window.location.pathname.includes("denomination"),
                    denominationId: selectedDenomination?.id
                  },
                  {
                    onSuccess: () => {
                      if (window.location.pathname.includes("denomination")) {
                        navigate(
                          `/denomination-dashboard/${selectedDenomination?.id}/hr`
                        );
                      } else {
                        navigate(`/hr`);
                      }
                    }
                  }
                )
              }
              disabled={deleteEmployeeMutation.isLoading}
            >
              {t("form.common.delete")}
            </Button>
          </Stack>
          <Typography variant="subtitle1" color="primary.main">
            {employee?.position}
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="repeat(2, 1fr)"
            gridTemplateRows="1fr"
          >
            <Stack spacing={0.5}>
              <Typography>
                {t("HR.HR.EmployeeID")}: {employee?.employee_id}
              </Typography>
              <Typography>
                {t("HR.Adress.Phone Number")}: {employee?.phone_number}
              </Typography>
              <Typography>
                {t("HR.Adress.Email")}: {employee?.email}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Typography>
                {t("HR.PersonalInfo.DateOfBirth")}:{" "}
                {employee?.date_of_birth
                  ? moment(employee.date_of_birth).format("YYYY-MM-DD")
                  : ""}
              </Typography>
              <Typography>
                {t("HR.PersonalInfo.MaritalStatus")}: {employee?.marital_status}
              </Typography>
              <Typography>
                {t("HR.PersonalInfo.Nationality")}:{" "}
                {employee?.nationality?.nationality}
              </Typography>
            </Stack>
          </Box>
          <Stack
            alignItems="flex-start"
            visibility={denominationId ? "hidden" : "visible"}
          >
            <Button
              flavor="outlined"
              onClick={() => {
                clearRecipients();
                removeEmployee(employee?.id);
                addRecipient(
                  `${employee?.id}_${employee?.first_name}_${employee?.last_name}`,
                  employee
                );
                addEmployee(employee?.id, { id: employee.id });
                navigate("/messages");
              }}
            >
              {t("members.profile.SendMessage")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row">
        <Stack
          pr={4}
          sx={{ borderRight: "1px solid rgba(226, 232, 240, 1)" }}
          spacing={1}
        >
          <Typography
            sx={{
              bgcolor:
                activeNav === "employer" ? "neutral.main" : "transparent",
              p: 2,
              cursor: "pointer"
            }}
            onClick={() => setActiveNav("employer")}
          >
            {t("HR.AddEmployee.EmployerInformation")}
          </Typography>
          <Typography
            sx={{
              bgcolor:
                activeNav === "personal" ? "neutral.main" : "transparent",
              p: 2,
              cursor: "pointer"
            }}
            onClick={() => setActiveNav("personal")}
          >
            {t("HR.AddEmployee.PersonalInfo")}
          </Typography>
          <Typography
            sx={{
              bgcolor: activeNav === "family" ? "neutral.main" : "transparent",
              p: 2,
              cursor: "pointer"
            }}
            onClick={() => setActiveNav("family")}
          >
            {t("HR.AddEmployee.FamilyInformation")}
          </Typography>
          <Typography
            sx={{
              bgcolor:
                activeNav === "education" ? "neutral.main" : "transparent",
              p: 2,
              cursor: "pointer"
            }}
            onClick={() => setActiveNav("education")}
          >
            {t("HR.AddEmployee.EducationBackground")}
          </Typography>
          <Typography
            sx={{
              bgcolor: activeNav === "job" ? "neutral.main" : "transparent",
              p: 2,
              cursor: "pointer"
            }}
            onClick={() => setActiveNav("job")}
          >
            {t("HR.AddEmployee.Job")}
          </Typography>
          <Typography
            sx={{
              bgcolor: activeNav === "bank" ? "neutral.main" : "transparent",
              p: 2,
              cursor: "pointer"
            }}
            onClick={() => setActiveNav("bank")}
          >
            {t("HR.AddEmployee.BankandTIN")}
          </Typography>
          <Typography
            sx={{
              bgcolor:
                activeNav === "compensation" ? "neutral.main" : "transparent",
              p: 2,
              cursor: "pointer"
            }}
            onClick={() => setActiveNav("compensation")}
          >
            {t("HR.AddEmployee.Compensations")}
          </Typography>
          <Typography
            sx={{
              bgcolor:
                activeNav === "emergency" ? "neutral.main" : "transparent",
              p: 2,
              cursor: "pointer"
            }}
            onClick={() => setActiveNav("emergency")}
          >
            {t("HR.AddEmployee.EmergencyContact")}
          </Typography>
        </Stack>
        <Stack pl={2} flexBasis="60%">
          {activeNav === "employer" && (
            <EmployerInfoForm
              register={register}
              errors={errors}
              control={control}
              denominationId={
                window.location.pathname.includes("denomination")
                  ? selectedDenomination?.id
                  : undefined
              }
              watch={watch}
              setValue={setValue}
            />
          )}
          {activeNav === "personal" && (
            <PersonalInfoForm
              register={register}
              errors={errors}
              control={control}
              setValue={setValue}
              watch={watch}
            />
          )}
          {activeNav === "family" && (
            <FamilyInfoForm
              register={register}
              errors={errors}
              control={control}
              watch={watch}
              setValue={setValue}
            />
          )}
          {activeNav === "education" && (
            <EducationBackgroundForm
              register={register}
              errors={errors}
              control={control}
              watch={watch}
              setValue={setValue}
            />
          )}
          {activeNav === "job" && (
            <JobForm
              register={register}
              errors={errors}
              control={control}
              watch={watch}
              setValue={setValue}
            />
          )}
          {activeNav === "bank" && (
            <BankForm register={register} errors={errors} watch={watch} />
          )}
          {activeNav === "compensation" && (
            <CompensationForm register={register} errors={errors} />
          )}
          {activeNav === "emergency" && (
            <EmergencyContactForm
              register={register}
              errors={errors}
              control={control}
            />
          )}

          <MutationError error={updateEmployeeMutation.error} sx={{ mt: 2 }} />

          <Stack alignItems="flex-end" mt={3}>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={updateEmployeeMutation.isLoading}
            >
              {t("users.userProfile.updateChanges")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <Notification />
    </Stack>
  );
};
